/**
 * Budget Pacing tab. Spend vs Planner remaining, autonomous mix, Save split.
 * Strategy Yes/No lives on Management (`BudgetStrategyPanel`).
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Activity,
  CalendarDays,
  Loader2,
  RefreshCw,
  Settings,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TablePagination, usePagination } from '@/components/ui/data-table';
import { adsDaysSince, adsRelativeAge } from '@/lib/adsMetricFormat';
import { ChannelSplitCard } from '@/components/clients/ContractOpsCards';
import { clientsService } from '@/services/clientsService';

/**
 * History window for the pacing snapshot. 'all' = every cycle the Planner
 * has. CORE-BP-04: labels moved from "3 cycles" to "3 months" — a billing
 * cycle IS a month here (the anchor is the 1st or 15th; a cycle = anchor
 * to next anchor = one month), and operators read "cycles" and "months"
 * as two different concepts. Same underlying values.
 */
export type HistoryCycles = '3' | '6' | '12' | '24' | 'all';
export const HISTORY_CYCLE_OPTIONS: Array<{ value: HistoryCycles; label: string }> = [
  { value: '3', label: '3 months' },
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
  { value: '24', label: '24 months' },
  { value: 'all', label: 'All history' },
];
// The API reads 0 as "every cycle with Planner data".
const historyCyclesParam = (value: HistoryCycles): number =>
  value === 'all' ? 0 : Number(value);

/** CORE-BP-04: URL param that pins the selector so a refresh or shared
 *  Slack link keeps the operator's chosen window. */
export const HISTORY_CYCLES_URL_PARAM = 'cycles';
export const DEFAULT_HISTORY_CYCLES_VALUE: HistoryCycles = '6';

/**
 * Parse a URL value into a valid HistoryCycles or return null so the caller
 * can fall through to the default. Anything unrecognised is treated as
 * absent — an unknown value should not silently pin the fetch.
 */
export function parseHistoryCyclesParam(
  raw: string | null | undefined,
): HistoryCycles | null {
  if (!raw) return null;
  const match = HISTORY_CYCLE_OPTIONS.find((o) => o.value === raw);
  return match ? match.value : null;
}

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  getBudgetPacing,
  getGoogleBudgetSplit,
  getPacingAccountScope,
  updateGoogleBudgetSplit,
  updatePacingAccountScope,
  updatePacingBudget,
  type AutonomousPacingLsa,
  type BudgetPacingCampaignRow,
  type BudgetPacingCycle,
  type BudgetPacingResponse,
  type PacingAccountScope,
} from '@/services/googleAdsMonthlyService';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import {
  BudgetPacingBadge,
  campaignChannelLabel,
  isSearchChannel,
  lsaWeeklyRecommendationMicros,
} from './budgetPacing';
import { AutonomousPacingPreviewCard, AUTONOMOUS_PACING_QUERY_KEY } from './AutonomousPacingPreviewCard';
import { AutomatedPacingSwitch } from './AutomatedPacingSwitch';
import { BudgetMoneyStrip, type AutonomousPacingSummary } from './BudgetMoneyStrip';
import { listTypeSetAsideItems } from '@/services/googleAdsMonthlyService';
import { PERSISTED_GC_TIME_MS, cacheAgeLabel, warmCacheNote } from '@/lib/budgetPacingPersist';
import {
  BudgetPacingAccountSettings,
  formatLinkedCustomerId,
} from './BudgetPacingAccountSettings';
import { CampaignBudgetsCard } from './CampaignBudgetsCard';

/**
 * `renderContext` tells this component which mount site it is on so its
 * cross-page copy doesn't lie:
 *  - 'standalone-pacing' (default): standalone `/ads-tools/budgets` page,
 *    where Strategy Yes/No really is on a different page (Management).
 *  - 'management-subtab': mounted inside Management (`section === 'budgets'`),
 *    where "Strategy Yes/No is on Management, not this page" reads as
 *    self-contradicting copy — swap for sibling-tab framing. (CORE-M-08.c)
 */
type BudgetReallocationRenderContext = 'standalone-pacing' | 'management-subtab';

interface BudgetReallocationTabProps {
  tenantId: string;
  renderContext?: BudgetReallocationRenderContext;
}

export function snapshotLsaMonitor(
  snapshot?: BudgetPacingResponse,
): AutonomousPacingLsa | null {
  if (!snapshot?.has_lsa) return null;
  const cycle = snapshot.cycles[snapshot.current_cycle_index];
  const totals = cycle?.totals;
  if (totals?.lsa_remaining_usd == null) {
    return {
      customer_id: snapshot.lsa_customer_id ?? null,
      available: false,
      budget_micros: null,
      spent_micros: null,
      remaining_micros: null,
      implied_daily_micros: null,
      weekly_recommendation_micros: null,
    };
  }
  const remainingUsd = totals.lsa_remaining_usd ?? 0;
  const daysLeft = cycle?.days_left ?? 0;
  const impliedUsd =
    remainingUsd > 0 && daysLeft > 0 ? remainingUsd / daysLeft : 0;
  const remainingMicros = Math.round(remainingUsd * 1_000_000);
  return {
    customer_id: snapshot.lsa_customer_id ?? null,
    available: true,
    budget_micros: Math.round((totals?.lsa_budget_usd ?? 0) * 1_000_000),
    spent_micros: Math.round((totals?.lsa_spent_usd ?? 0) * 1_000_000),
    remaining_micros: remainingMicros,
    implied_daily_micros: Math.round(impliedUsd * 1_000_000),
    // Same math and same cycle.days_left the backend producer uses, so the
    // rec is on the row at page load, before any preview exists.
    weekly_recommendation_micros: lsaWeeklyRecommendationMicros(
      remainingMicros,
      daysLeft,
    ),
  };
}



function GoogleAdsCustomerIdSetupNotice({ tenantId }: { tenantId: string }) {
  return (
    <div className="rounded border border-coral/30 bg-coral-light/20 p-4 text-sm text-coral-dark">
      <div className="mb-2 flex items-center gap-2 font-semibold text-coral-dark">
        <TriangleAlert className="h-4 w-4" />
        Google Ads account mapping required
      </div>
      <p className="mb-3">
        Budget pacing needs the Google Ads customer ID for {tenantId} before it
        can pull live campaign budgets and spend. If this tenant is mapped to
        Planner, save the Ads customer ID in Account settings on this tab.
        Otherwise add it in Workspace Settings, then refresh.
      </p>
      <ol className="mb-3 list-decimal space-y-1 pl-5">
        <li>Open Workspace Settings.</li>
        <li>Go to Integrations, then Google Ads.</li>
        <li>Paste the client customer ID from Google Ads, digits or dashes.</li>
        <li>Save settings and refresh Budget Pacing.</li>
      </ol>
      <Button asChild type="button" variant="outline" size="sm">
        <Link to="/workspace-settings?tab=integrations">
          <Settings className="mr-2 h-4 w-4" />
          Open Google Ads settings
        </Link>
      </Button>
    </div>
  );
}

function GoogleAdsOAuthSetupNotice() {
  return (
    <div className="rounded border border-coral/30 bg-coral-light/20 p-4 text-sm text-coral-dark">
      <div className="mb-2 flex items-center gap-2 font-semibold text-coral-dark">
        <TriangleAlert className="h-4 w-4" />
        Google Ads connection required
      </div>
      <p className="mb-3">
        Budget pacing needs an active Google Ads OAuth connection before it can
        pull live campaign budgets and spend.
      </p>
      <ol className="mb-3 list-decimal space-y-1 pl-5">
        <li>Open Workspace Settings.</li>
        <li>Go to Integrations, then Google Ads.</li>
        <li>Connect or reconnect Google Ads.</li>
        <li>Return here and refresh Budget Pacing.</li>
      </ol>
      <Button asChild type="button" variant="outline" size="sm">
        <Link to="/workspace-settings?tab=integrations">
          <Settings className="mr-2 h-4 w-4" />
          Open Google Ads settings
        </Link>
      </Button>
    </div>
  );
}

export function BudgetReallocationTab({
  tenantId,
  renderContext = 'standalone-pacing',
}: BudgetReallocationTabProps) {
  const insideManagement = renderContext === 'management-subtab';
  const [autonomousSummary, setAutonomousSummary] = useState<AutonomousPacingSummary | null>(null);
  const [cycleStartDay, setCycleStartDay] = useState<'1' | '15'>('1');
  // CORE-BP-04: the selector state is mirrored to the URL as ?cycles=N so
  // a refresh or shared Slack link keeps the operator's chosen window.
  // Initial value comes from the URL when present; unknown values fall
  // through to the '6 months' default rather than pinning a broken read.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlHistoryCycles = parseHistoryCyclesParam(
    searchParams.get(HISTORY_CYCLES_URL_PARAM),
  );
  const [historyCycles, setHistoryCycles] = useState<HistoryCycles>(
    urlHistoryCycles ?? DEFAULT_HISTORY_CYCLES_VALUE,
  );
  const handleHistoryCyclesChange = (value: HistoryCycles) => {
    setHistoryCycles(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_HISTORY_CYCLES_VALUE) {
          next.delete(HISTORY_CYCLES_URL_PARAM);
        } else {
          next.set(HISTORY_CYCLES_URL_PARAM, value);
        }
        return next;
      },
      { replace: true },
    );
  };
  // Bumped after an account settings save. The pacing preview query is
  // disabled once seeded, so invalidateQueries never reaches it; the card
  // refetches through its own observer when this token changes.
  const [planRefreshToken, setPlanRefreshToken] = useState(0);
  // Server clock of the last save; the card only lets plans saved after it
  // paint over the fresh preview.
  const [scopeSavedAt, setScopeSavedAt] = useState<string | null>(null);
  // True only while the card's post-save refetch is on the wire (the card
  // reports it), so a later manual refresh never says "applying settings".
  const [planRefreshing, setPlanRefreshing] = useState(false);
  // "Change split" on the pacing card's LSA row opens Account settings, which
  // owns its own trigger; bumping this signal opens it from here.
  const [accountSettingsOpenSignal, setAccountSettingsOpenSignal] = useState(0);
  const { data: tenantSettings, isLoading: tenantSettingsLoading } =
    useTenantSettings(tenantId);
  const googleAdsCustomerId = tenantSettings?.google_ad_customer_id?.trim() ?? '';
  // The two Google-only cards below fire Google reads on mount, so they wait
  // for settings and need a customer id (the mapping notice covers the rest).
  const hasGoogleAdsId = !tenantSettingsLoading && Boolean(googleAdsCustomerId);
  const queryClient = useQueryClient();

  const pacingQuery = useQuery({
    queryKey: ['google-ads-budget-pacing', tenantId, cycleStartDay, historyCycles],
    queryFn: () =>
      getBudgetPacing({
        tenantId,
        cycleStartDay: Number(cycleStartDay) as 1 | 15,
        historyCycles: historyCyclesParam(historyCycles),
      }),
    enabled: Boolean(googleAdsCustomerId),
    staleTime: 60_000,
    gcTime: PERSISTED_GC_TIME_MS,
  });

  const accountScopeQuery = useQuery({
    queryKey: ['google-ads-pacing-account-scope', tenantId],
    queryFn: () => getPacingAccountScope({ tenantId }),
    enabled: Boolean(tenantId),
    gcTime: PERSISTED_GC_TIME_MS,
    retry: false,
    staleTime: 60_000,
  });
  const accountScopeUnmapped =
    accountScopeQuery.isError &&
    (accountScopeQuery.error as { response?: { status?: number } })?.response
      ?.status === 400;

  const currentCycle = pacingQuery.data
    ? pacingQuery.data.cycles[pacingQuery.data.current_cycle_index] ?? null
    : null;

  // The two Google monthly lines for the split control. Keyed by cycle so the
  // per-cycle override flags follow the cycle the operator is looking at.
  const splitQuery = useQuery({
    queryKey: ['google-ads-pacing-google-split', tenantId, currentCycle?.start ?? null],
    queryFn: () =>
      getGoogleBudgetSplit({ tenantId, billingPeriodStart: currentCycle?.start ?? null }),
    enabled: accountScopeQuery.isSuccess,
    retry: false,
    staleTime: 60_000,
  });

  const saveAccountScope = useMutation({
    mutationFn: async (next: {
      adsCustomerId: string;
      lsaCustomerId: string;
      campaignNameFilter: string;
      includedCampaignIds: string[] | null;
      lsaAutopace: boolean | null;
      combinedTotals: boolean | null;
      lsaMonthlyDollars: number | null;
    }): Promise<PacingAccountScope> => {
      const scopeResult = await updatePacingAccountScope({
        tenantId,
        adsCustomerId: next.adsCustomerId,
        lsaCustomerId: next.lsaCustomerId,
        campaignNameFilter: next.campaignNameFilter,
        includedCampaignIds: next.includedCampaignIds,
        lsaAutopace: next.lsaAutopace,
        combinedTotals: next.combinedTotals,
      });
      // The split is a second Planner write, after the account settings. Only
      // when the operator typed a different LSA amount; the backend refuses
      // bad values with a detail we surface below.
      const currentLsa = splitQuery.data?.google_lsa_monthly_dollars ?? null;
      if (next.lsaMonthlyDollars != null && next.lsaMonthlyDollars !== currentLsa) {
        let splitResult;
        try {
          splitResult = await updateGoogleBudgetSplit({
            tenantId,
            platform: 'Google LSA',
            monthlyDollars: next.lsaMonthlyDollars,
            billingPeriodStart: currentCycle?.start ?? null,
            // The total the dialog showed; the server refuses if it moved since.
            expectedTotalDollars: splitQuery.data?.total_monthly_dollars ?? null,
          });
        } catch (err) {
          throw Object.assign(err instanceof Error ? err : new Error(String(err)), {
            budgetSplitFailed: true,
          });
        }
        return {
          ...scopeResult,
          saved_at: laterIso(scopeResult.saved_at, splitResult.saved_at),
        };
      }
      return scopeResult;
    },
    onSuccess: (data) => {
      toast.success('Saved Ads account settings to Planner');
      void queryClient.invalidateQueries({
        queryKey: ['google-ads-pacing-account-scope', tenantId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['google-ads-pacing-google-split', tenantId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['google-ads-budget-pacing', tenantId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['tenantSettings', tenantId],
      });
      // No invalidateQueries for the autonomous preview: that query is
      // disabled once seeded and would ignore it. The token below makes the
      // card refetch through its own observer.
      setScopeSavedAt(data?.saved_at ?? null);
      setPlanRefreshToken((t) => t + 1);
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      if ((err as { budgetSplitFailed?: boolean })?.budgetSplitFailed) {
        // The account settings did land; only the second write was refused.
        void queryClient.invalidateQueries({
          queryKey: ['google-ads-pacing-account-scope', tenantId],
        });
        toast.error(`Account settings saved, but the budget split was not: ${detail}`);
        return;
      }
      toast.error(`Could not save account settings: ${detail}`);
    },
  });

  const savePacingBudget = useMutation({
    mutationFn: (budgetUsd: number) => {
      const snapshot = pacingQuery.data;
      const current = snapshot?.cycles[snapshot.current_cycle_index];
      if (!current?.start) {
        throw new Error('No current billing cycle to save against');
      }
      return updatePacingBudget({
        tenantId,
        budgetUsd,
        billingPeriodStart: current.start,
      });
    },
    onSuccess: () => {
      toast.success('Saved pacing budget to Planner');
      void queryClient.invalidateQueries({
        queryKey: ['google-ads-budget-pacing', tenantId],
      });
      void queryClient.invalidateQueries({
        queryKey: [AUTONOMOUS_PACING_QUERY_KEY, tenantId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Could not save budget: ${detail}`);
    },
  });

  // Suggestions live on Management; this page only says one is waiting.
  const setAsideItems = useQuery({
    queryKey: ['google-ads-type-set-aside', tenantId],
    queryFn: () => listTypeSetAsideItems(tenantId),
    // Core-DB poll (suggestions waiting): only with a customer id, i.e. once
    // settings have loaded and the client has a Google Ads account.
    enabled: Boolean(tenantId) && hasGoogleAdsId,
    refetchInterval: 60_000,
    retry: false,
  });
  const waiting = setAsideItems.data?.length ?? 0;
  // Saving, or the post-save preview is still on the wire: the daily tiles
  // say Recalculating instead of the pre-save money.
  const recalculating = saveAccountScope.isPending || planRefreshing;
  // Figures older than staleTime carry their age whatever the refetch is
  // doing; a failed refresh must not leave yesterday's numbers looking live.
  const cachedNote = pacingQuery.data
    ? warmCacheNote(cacheAgeLabel(pacingQuery.dataUpdatedAt), {
        isFetching: pacingQuery.isFetching,
        isError: pacingQuery.isError,
      })
    : null;
  return (
    <div className="relative space-y-6">
      {cachedNote ? (
        <p
          className={`text-xs ${pacingQuery.isError ? 'text-coral-dark' : 'text-muted-foreground'}`}
          data-testid="warm-cache-note"
        >
          {cachedNote}
        </p>
      ) : null}
      <BudgetMoneyStrip
        cycle={currentCycle}
        loading={pacingQuery.isLoading}
        recalculating={recalculating}
        billingCycleStartDay={pacingQuery.data?.billing_cycle_start_day}
        spendSource={pacingQuery.data?.spend_source}
        plannerSourced={pacingQuery.data?.budget_source === 'planner_contract'}
        daysLeftInclusive={currentCycle?.days_left_inclusive}
        autonomous={autonomousSummary}
        canEditBudget={pacingQuery.data?.budget_source === 'planner_contract'}
        savingBudget={savePacingBudget.isPending}
        onSaveBudget={
          pacingQuery.data?.budget_source === 'planner_contract'
            ? (budgetUsd) => savePacingBudget.mutate(budgetUsd)
            : undefined
        }
      />
      {/* 46062138: staff-only; renders nothing for anyone else. */}
      <AutomatedPacingSwitch tenantId={tenantId} />
      {waiting > 0 ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-coral/40 bg-coral-light px-4 py-2 text-sm"
          data-testid="set-aside-notice"
        >
          <span>
            {waiting} budget suggestion{waiting === 1 ? ' is' : 's are'} waiting for this client on Management.
          </span>
          <Link className="font-medium text-coral-dark underline-offset-2 hover:underline" to="/ads-tools/management?platform=google-ads">
            Open Management
          </Link>
        </div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        {insideManagement ? (
          <p className="text-sm text-muted-foreground">
            Strategy Yes/No lives on the{' '}
            <Link
              className="text-coral underline-offset-2 hover:underline"
              to="/ads-tools/management?platform=google-ads"
            >
              Management overview
            </Link>
            , not on this Budget Reallocation subtab.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Strategy Yes/No is on{' '}
            <Link
              className="text-coral underline-offset-2 hover:underline"
              to="/ads-tools/management?platform=google-ads"
            >
              Management
            </Link>
            , not this page.
          </p>
        )}
        <BudgetPacingAccountSettings
          tenantCustomerId={googleAdsCustomerId}
          scope={accountScopeQuery.data ?? null}
          scopeLoading={accountScopeQuery.isLoading}
          mapped={accountScopeQuery.isSuccess}
          unmapped={accountScopeUnmapped}
          canEdit={accountScopeQuery.isSuccess}
          saving={Boolean(saveAccountScope.isPending)}
          onSave={accountScopeQuery.isSuccess
            ? async (next) => {
                await saveAccountScope.mutateAsync(next);
              }
            : undefined}
          snapshotScope={pacingQuery.data?.campaign_scope}
          split={splitQuery.data ?? null}
          splitLoading={splitQuery.isLoading}
          openSignal={accountSettingsOpenSignal}
        />
      </div>
      {pacingQuery.data?.budget_source === 'planner_no_google' || !hasGoogleAdsId ? null : (
      <AutonomousPacingPreviewCard
        tenantId={tenantId}
        combinedTotals={Boolean(accountScopeQuery.data?.combined_totals)}
        onSummaryChange={setAutonomousSummary}
        refreshToken={planRefreshToken}
        scopeSavedAt={scopeSavedAt}
        onScopeRefreshingChange={setPlanRefreshing}
        googleSplit={splitQuery.data ?? null}
        onChangeSplit={() => setAccountSettingsOpenSignal((n) => n + 1)}
        accountTimeZone={pacingQuery.data?.account_time_zone}
        cycleDaysLeft={
          pacingQuery.data
            ? pacingQuery.data.cycles[pacingQuery.data.current_cycle_index]
                ?.days_left
            : undefined
        }
        cycleStart={
          pacingQuery.data
            ? pacingQuery.data.cycles[pacingQuery.data.current_cycle_index]
                ?.start
            : undefined
        }
        cycleEnd={
          pacingQuery.data
            ? pacingQuery.data.cycles[pacingQuery.data.current_cycle_index]
                ?.end
            : undefined
        }
        snapshotLsa={snapshotLsaMonitor(pacingQuery.data)}
      />
      )}

      {pacingQuery.data?.budget_source === 'planner_no_google' || !hasGoogleAdsId ? null : (
        <CampaignBudgetsCard
          tenantId={tenantId}
          /* Campaign identity comes from the pacing plan rather than a second
             fetch, so this card can only ever offer campaigns pacing already
             knows about — a budget for something out of scope would be
             orphaned on the first tick. */
          campaigns={(
            queryClient.getQueryData<{
              adjustments?: Array<{ campaign_id: string; campaign_name: string }>;
            }>([AUTONOMOUS_PACING_QUERY_KEY, tenantId])?.adjustments ?? []
          ).map((a) => ({
            campaign_id: a.campaign_id,
            campaign_name: a.campaign_name,
          }))}
          contractedMonthlyUsd={
            pacingQuery.data
              ? pacingQuery.data.cycles[pacingQuery.data.current_cycle_index]
                  ?.totals?.budget_usd
              : undefined
          }
          onSaved={() => {
            // The next plan must be recomputed against the new budgets;
            // showing the pre-save split would misreport what will run.
            queryClient.invalidateQueries({
              queryKey: [AUTONOMOUS_PACING_QUERY_KEY, tenantId],
            });
          }}
        />
      )}

      <BudgetPacingCard
        tenantId={tenantId}
        snapshot={pacingQuery.data ?? null}
        loading={pacingQuery.isLoading}
        settingsLoading={tenantSettingsLoading}
        error={pacingQuery.error}
        refreshing={pacingQuery.isFetching && !pacingQuery.isLoading}
        googleAdsCustomerId={googleAdsCustomerId}
        cycleStartDay={cycleStartDay}
        historyCycles={historyCycles}
        onCycleStartDayChange={setCycleStartDay}
        onHistoryCyclesChange={handleHistoryCyclesChange}
        onRefresh={() => pacingQuery.refetch()}
        accountScope={accountScopeQuery.data ?? null}
        accountScopeLoading={accountScopeQuery.isLoading}
        accountScopeMapped={accountScopeQuery.isSuccess}
        accountScopeUnmapped={accountScopeUnmapped}
        onSaveAccountScope={
          accountScopeQuery.isSuccess
            ? async (next) => {
                await saveAccountScope.mutateAsync(next);
              }
            : undefined
        }
        savingAccountScope={saveAccountScope.isPending}
      />
      <ChannelSplitForTenant tenantId={tenantId} />
    </div>
  );
}

/** The later of two server clocks; either may be missing. */
function laterIso(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}

function formatUsd(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value ?? 0);
}


/**
 * Render the ChannelSplitCard on the pacing page for tenants that have a
 * contract. Resolves tenantId (a website) to contract_id through the roster;
 * the roster query is cached so mounting on multiple pages doesn't refetch.
 * Renders nothing when the tenant has no contract (e.g. Lanracorp Hiring).
 */
function ChannelSplitForTenant({ tenantId }: { tenantId: string }) {
  const roster = useQuery({
    queryKey: ['clients', 'roster'],
    queryFn: () => clientsService.roster(),
    staleTime: 5 * 60_000,
  });
  const contractId = roster.data?.clients
    .find((c) => c.contract_id && c.tenants.some((t) => t.tenant_id === tenantId))
    ?.contract_id;
  if (!contractId) return null;
  return <ChannelSplitCard contractId={contractId} onApplied={() => {}} />;
}

function BudgetPacingCard({
  tenantId,
  snapshot,
  loading,
  settingsLoading,
  error,
  refreshing,
  googleAdsCustomerId,
  cycleStartDay,
  historyCycles,
  onCycleStartDayChange,
  onHistoryCyclesChange,
  onRefresh,
  accountScope,
  accountScopeLoading,
  accountScopeMapped,
  accountScopeUnmapped,
  onSaveAccountScope,
  savingAccountScope,
}: {
  tenantId: string;
  snapshot: BudgetPacingResponse | null;
  loading: boolean;
  settingsLoading: boolean;
  error: unknown;
  refreshing: boolean;
  googleAdsCustomerId: string;
  cycleStartDay: '1' | '15';
  historyCycles: HistoryCycles;
  onCycleStartDayChange: (value: '1' | '15') => void;
  onHistoryCyclesChange: (value: HistoryCycles) => void;
  onRefresh: () => void;
  accountScope: PacingAccountScope | null;
  accountScopeLoading: boolean;
  accountScopeMapped: boolean;
  accountScopeUnmapped: boolean;
  onSaveAccountScope?: (next: {
    adsCustomerId: string;
    lsaCustomerId: string;
    campaignNameFilter: string;
    includedCampaignIds: string[] | null;
    lsaAutopace: boolean | null;
    combinedTotals: boolean | null;
    /** New Google LSA monthly line; null = unchanged. Kept in step with
     *  BudgetPacingAccountSettings.onSave, which this forwards to. */
    lsaMonthlyDollars: number | null;
  }) => void | Promise<void>;
  savingAccountScope?: boolean;
}) {
  const current = snapshot?.cycles[snapshot.current_cycle_index] ?? null;
  // True when budget/spend/carryover come from the Agile Planner (a mapped
  // client) rather than live Google Ads — drives the source copy below.
  const plannerSourced = snapshot?.budget_source === 'planner_contract';
  const noGooglePacing = snapshot?.budget_source === 'planner_no_google';
  const history = snapshot?.cycles
    .slice(0, snapshot.current_cycle_index)
    .reverse() ?? [];
  const errorMessage =
    (error as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? (error instanceof Error ? error.message : null);
  const missingCustomerId =
    !googleAdsCustomerId ||
    Boolean(errorMessage?.includes('no google_ad_customer_id'));
  const missingOAuth =
    Boolean(errorMessage?.includes('Google Ads OAuth')) ||
    Boolean(errorMessage?.includes('Google Ads API adapter is not configured'));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-teal" />
              Budget pacing
              {current ? <BudgetPacingBadge status={current.totals.pacing_status} /> : null}
            </CardTitle>
            <CardDescription>
              {noGooglePacing
                ? `Google Ads is not in this client's Planner pacing. Leftover is on the live platform in Planner, not here.`
                : plannerSourced
                  ? `Budget, spend and carryover from the Agile Planner (current-cycle headline) for ${tenantId}.`
                  : `Current-cycle pacing from live Google Ads budgets and spend for ${tenantId}.`}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {plannerSourced || noGooglePacing ? (
              <Badge variant="outline">
                Bills on {snapshot?.billing_cycle_start_day === 15 ? '15th' : '1st'}
              </Badge>
            ) : (
              <Select
                value={cycleStartDay}
                onValueChange={(value) => onCycleStartDayChange(value as '1' | '15')}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Bills on 1st</SelectItem>
                  <SelectItem value="15">Bills on 15th</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select
              value={historyCycles}
              onValueChange={(value) => onHistoryCyclesChange(value as HistoryCycles)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HISTORY_CYCLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={onRefresh} disabled={loading || refreshing}>
              {refreshing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded border border-border bg-warm-bg px-3 py-2 text-sm text-muted-foreground">
          {noGooglePacing
            ? 'This client still has a Google Ads account, but Planner pacing for them is on another platform. Removing Google Ads from pacing is what lets leftover land on the live channel instead of a $0 Ads row.'
              : plannerSourced
              ? 'Budget, spend and carryover come from the Agile Planner. Edit Budget on the cards below to change the contract. Ads and LSA account links are under Account settings.'
              : 'This tenant is not mapped to Planner, so the cards use live Google Ads dailies. Map the client to edit the contract budget and account links here.'}
        </div>
        {accountScope?.ads_customer_id || accountScope?.lsa_customer_id ? (
          <p className="text-xs text-muted-foreground">
            Ads {formatLinkedCustomerId(accountScope.ads_customer_id)}
            {accountScope.lsa_customer_id
              ? ` · LSA ${formatLinkedCustomerId(accountScope.lsa_customer_id)}`
              : ''}
          </p>
        ) : null}
        {settingsLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking Google Ads setup…
          </div>
        ) : missingCustomerId ? (
          <GoogleAdsCustomerIdSetupNotice tenantId={tenantId} />
        ) : missingOAuth ? (
          <GoogleAdsOAuthSetupNotice />
        ) : loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading pacing…
          </div>
        ) : errorMessage ? (
          <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {errorMessage}
          </div>
        ) : noGooglePacing || !current ? (
          <div className="rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
            {noGooglePacing
              ? 'No Google Ads leftover to pace. Open this client in Planner to see rollover on the funded platform.'
              : 'No pacing snapshot returned.'}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5" />
              <span>
                {current.label} · {current.start} → {current.end}
              </span>
              <Badge variant="outline">
                {current.days_left > 0
                  ? `${current.days_left} day${current.days_left === 1 ? '' : 's'} left`
                  : 'Cycle ended'}
              </Badge>
              <span>CID {snapshot?.customer_id}</span>
              {snapshot?.lsa_customer_id ? (
                <span>LSA {snapshot.lsa_customer_id}</span>
              ) : null}
              {snapshot?.spend_source === 'planner_recorded' ? (
                <Badge variant="outline">Spend from Planner</Badge>
              ) : snapshot?.spend_source === 'live_google_ads' ? (
                <Badge variant="outline">Spend from live Ads</Badge>
              ) : null}
            </div>

            <BudgetPacingPlatformBreakdown cycles={snapshot?.cycles ?? []} />

            <BudgetPacingPolicyPanel rows={current.rows} />

            <BudgetPacingCampaignTable rows={current.rows} plannerSourced={plannerSourced} />

            {history.length > 0 ? <BudgetPacingHistoryTable cycles={history} /> : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

const HISTORY_PAGE_SIZES = [6, 12, 24];

/** Hand-entered spend older than this should not be paced off unexamined. */
const SPEND_STALE_DAYS = 3;

/**
 * Age of a hand-entered spend figure, under the Spent number.
 *
 * Renders nothing unless the backend supplied `spend_recorded_at`, which it
 * does for hand-entered platforms ONLY — Meta, Bing, Reddit. Google Ads and LSA
 * are pulled automatically upstream, and the timestamp Core holds for them
 * records when the figure last CHANGED rather than when it was checked: a
 * paused account sits unchanged for weeks with current data. The backend sends
 * null there, so this shows nothing rather than a false staleness warning.
 *
 * The wording says "entered", never "synced": what period a hand-typed number
 * covers is not recorded anywhere, so claiming a pull happened would be a
 * different false impression.
 */
function SpendAge({ recordedAt }: { recordedAt?: string | null }) {
  const ago = adsRelativeAge(recordedAt);
  if (!ago) return null;
  const stale = (adsDaysSince(recordedAt) ?? 0) >= SPEND_STALE_DAYS;
  return (
    <span
      className={`block text-[10px] font-normal ${
        stale ? 'text-destructive' : 'text-muted-foreground'
      }`}
      title={
        stale
          ? `Typed in by hand ${ago} and not touched since. Nothing refreshes this platform automatically, and the period the figure covers isn't recorded — confirm before pacing off it.`
          : `Typed in by hand ${ago}. This platform has no automatic pull.`
      }
    >
      entered {ago}
    </span>
  );
}

function BudgetPacingPlatformBreakdown({ cycles }: { cycles: BudgetPacingCycle[] }) {
  const withPlatforms = cycles.filter((c) => (c.platforms?.length ?? 0) > 0);
  // Newest cycle first (current at the top). Paged by cycle, not by row, so a
  // cycle's platforms never split across pages.
  const ordered = [...withPlatforms].reverse();
  const { page, pageSize, setPage, setPageSize, pageCount, slice } = usePagination(
    ordered.length,
    { defaultPageSize: HISTORY_PAGE_SIZES[0] },
  );
  if (withPlatforms.length === 0) return null;
  const platformNames = new Set(
    withPlatforms.flatMap((c) => (c.platforms ?? []).map((p) => p.platform)),
  );
  const hasOtherPlatforms = [...platformNames].some(
    (name) => name !== 'Google Ads' && name !== 'Google LSA',
  );
  const pageCycles = slice(ordered);
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <div className="border-b border-border bg-warm-bg/60 px-3 py-2 text-sm font-medium">
        By platform, per cycle
        <span className="ml-2 text-xs font-normal text-muted-foreground">
          {hasOtherPlatforms
            ? 'Ads, LSA, and Meta/Bing — each counted in the Planner cycle total'
            : 'Google Ads + Google LSA — each counted in the cycle total'}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="px-3 py-1.5 text-left font-medium">Cycle</th>
              <th className="px-3 py-1.5 text-left font-medium">Platform</th>
              <th className="px-3 py-1.5 text-right font-medium">Budget</th>
              <th className="px-3 py-1.5 text-right font-medium">Spent</th>
              <th className="px-3 py-1.5 text-right font-medium">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {pageCycles.flatMap((c) =>
              (c.platforms ?? []).map((p, i) => (
                <tr key={`${c.label}-${p.platform}`} className="border-t border-border">
                  <td className="px-3 py-1.5 text-muted-foreground">{i === 0 ? c.label : ''}</td>
                  <td className="px-3 py-1.5">{p.platform}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatUsd(p.budget_usd)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatUsd(p.spent_usd)}
                    <SpendAge recordedAt={p.spend_recorded_at} />
                  </td>
                  <td
                    className={`px-3 py-1.5 text-right tabular-nums ${
                      p.remaining_usd < 0 ? 'text-destructive' : ''
                    }`}
                  >
                    {formatUsd(p.remaining_usd)}
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
      {ordered.length > HISTORY_PAGE_SIZES[0] ? (
        <TablePagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalRows={ordered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={HISTORY_PAGE_SIZES}
          className="border-t border-border px-3 py-2"
        />
      ) : null}
    </div>
  );
}



function formatWeekdays(days: string[] | undefined): string {
  if (!days || days.length === 0) return 'All days';
  if (days.length === 7) return 'All days';
  return days.map((day) => day.slice(0, 3)).join(', ');
}

function formatAdScheduleSource(source: string | undefined): string {
  return source === 'google_ads_ad_schedule'
    ? 'Google Ads schedule'
    : 'All days fallback';
}

function BudgetPacingPolicyPanel({ rows }: { rows: BudgetPacingCampaignRow[] }) {
  const budgetedRows = rows.filter((row) => row.daily_budget_usd > 0);
  if (budgetedRows.length === 0) return null;

  const scheduledRows = budgetedRows.filter(
    (row) => row.ad_schedule_source === 'google_ads_ad_schedule',
  );

  return (
    <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-300">
      <div className="mb-2 flex items-center gap-2 font-semibold">
        <TriangleAlert className="h-4 w-4" />
        Ad schedule budget pacing
      </div>
      <p className="mb-3 text-xs leading-5">
        Google paces each live campaign toward a monthly cap of daily budget x
        30.4 and can spend up to 2x the daily budget on an active day. Disabled
        ad-schedule days do not serve, so scheduled campaigns can spend above
        the displayed daily budget on active days.
      </p>
      <div className="overflow-x-auto rounded border border-amber-200 dark:border-amber-800 bg-card/70">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campaign</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Active weekdays</TableHead>
              <TableHead className="text-right">Active days/mo</TableHead>
              <TableHead className="text-right">Monthly cap</TableHead>
              <TableHead className="text-right">Active-day avg</TableHead>
              <TableHead className="text-right">2x daily cap</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {budgetedRows.map((row) => (
              <TableRow key={`schedule-${row.campaign_id}`}>
                <TableCell>
                  <div className="font-medium text-foreground">
                    {row.campaign_name || row.campaign_id}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {row.campaign_id}
                  </div>
                </TableCell>
                <TableCell>{formatAdScheduleSource(row.ad_schedule_source)}</TableCell>
                <TableCell>{formatWeekdays(row.active_weekdays)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {typeof row.active_service_days_per_month === 'number'
                    ? row.active_service_days_per_month.toLocaleString('en-US', {
                        maximumFractionDigits: 1,
                      })
                    : '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatUsd(row.google_monthly_cap_usd)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatUsd(row.active_day_average_usd)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatUsd(row.daily_spend_cap_usd)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {scheduledRows.length === 0 ? (
        <div className="mt-2 text-xs text-muted-foreground">
          No ad schedule rows were returned for these campaigns, so all days are
          treated as eligible.
        </div>
      ) : null}
    </div>
  );
}

function BudgetPacingCampaignTable({
  rows,
  plannerSourced,
}: {
  rows: BudgetPacingCampaignRow[];
  plannerSourced: boolean;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
        No enabled campaigns returned budget/spend rows for this cycle.
        {plannerSourced
          ? ' Account cards above still use the Agile Planner.'
          : ' Pacing totals stay at live Google Ads until campaigns exist.'}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Live Google Ads campaigns for this cycle. Autonomous pacing writes
        Search, PMax, and other Ads dailies (not LSA)
        {plannerSourced ? '; the Planner remaining sits on the cards above' : ''}.
      </p>
      <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campaign</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">Daily budget</TableHead>
            <TableHead className="text-right">Google monthly cap</TableHead>
            <TableHead className="text-right">Active-day avg</TableHead>
            <TableHead className="text-right">2x daily cap</TableHead>
            <TableHead className="text-right">Cycle budget</TableHead>
            <TableHead className="text-right">Spent</TableHead>
            <TableHead className="text-right">Remaining (live caps)</TableHead>
            {/* CORE-BP-06: renamed from "Suggested daily" (see the pacing
                metric block above). Same computed field, distinct label. */}
            <TableHead className="text-right">Pace today</TableHead>
            <TableHead className="text-right">Monthly rollover</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.campaign_id}>
              <TableCell>
                <div className="font-medium">{row.campaign_name || row.campaign_id}</div>
                <div className="text-xs text-muted-foreground">{row.campaign_id}</div>
                {(row.bidding_strategy_type || row.target_cpa_usd) && (
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {row.bidding_strategy_type || 'Bidding strategy unknown'}
                    {row.target_cpa_usd ? ` · tCPA ${formatUsd(row.target_cpa_usd)}` : ''}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <Badge variant="outline">
                    {campaignChannelLabel(row.advertising_channel_type)}
                  </Badge>
                  {!isSearchChannel(row.advertising_channel_type) ? (
                    <span className="text-[10px] text-muted-foreground">
                      Display only
                    </span>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(row.daily_budget_usd)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(row.google_monthly_cap_usd)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(row.active_day_average_usd)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(row.daily_spend_cap_usd)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(row.cycle_budget_usd)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(row.spend_usd)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(row.remaining_usd)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(row.suggested_daily_usd)}
              </TableCell>
              <TableCell
                className={`text-right tabular-nums ${
                  row.monthly_rollover_usd < 0 ? 'text-destructive' : 'text-[hsl(var(--status-success))]'
                }`}
              >
                {formatUsd(row.monthly_rollover_usd)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}

function BudgetPacingHistoryTable({ cycles }: { cycles: BudgetPacingCycle[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Cycle</TableHead>
            <TableHead className="text-right">Budget</TableHead>
            <TableHead className="text-right">Spent</TableHead>
            <TableHead className="text-right">Monthly rollover</TableHead>
            <TableHead className="text-right">Cumulative</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cycles.map((cycle) => (
            <TableRow key={cycle.start} className={!cycle.tracked ? 'opacity-60' : undefined}>
              <TableCell>
                <div className="font-medium">{cycle.label}</div>
                <div className="text-xs text-muted-foreground">
                  {cycle.start} → {cycle.end}
                </div>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(cycle.totals.budget_usd)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {cycle.tracked ? formatUsd(cycle.totals.spent_usd) : '—'}
              </TableCell>
              <TableCell
                className={`text-right tabular-nums ${
                  cycle.totals.monthly_rollover_usd < 0
                    ? 'text-destructive'
                    : 'text-[hsl(var(--status-success))]'
                }`}
              >
                {cycle.tracked ? formatUsd(cycle.totals.monthly_rollover_usd) : '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatUsd(cycle.totals.cumulative_rollover_usd)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

